// App-wide control styling matching the dashboard refresh (123dfae3): `lg` radii on
// interactive controls, semantic border/surface tokens, brand focus color. Encoding this
// at the theme level lifts every screen at once instead of repeating per-callsite props
// like the dashboard's `filterSelectProps`.
//
// Chakra's built-in gray Button/Badge variants use whiteAlpha overlays in dark mode
// (see @chakra-ui/theme button.mjs variantGhost / variantSolid). Those read as muddy
// neutral grey on our navy shell — override gray colorScheme to semantic blue-slate tokens.

import { defineStyle, defineStyleConfig, type StyleFunctionProps } from '@chakra-ui/react'
import { theme as chakraTheme } from '@chakra-ui/theme'

const fieldOverrides = {
  // Lives on the variant (not baseStyle) because Chakra applies size styles after
  // baseStyle, and size `md` would reset borderRadius back to `md`.
  borderRadius: 'lg',
  borderColor: 'border.control',
  bg: 'surface',
  _hover: { borderColor: 'border.strong' },
}

function runVariantFn(
  fn: ((props: StyleFunctionProps) => Record<string, unknown>) | Record<string, unknown> | undefined,
  props: StyleFunctionProps
): Record<string, unknown> {
  if (!fn) return {}
  if (typeof fn === 'function') return fn(props)
  return fn
}

const defaultButtonVariants = chakraTheme.components?.Button?.variants as
  | Record<
      string,
      ((props: StyleFunctionProps) => Record<string, unknown>) | Record<string, unknown>
    >
  | undefined

/** Shared neutral secondary control — navy-safe hover/active instead of whiteAlpha. */
const grayInteractive = {
  color: 'text.secondary',
  bg: 'transparent',
  _hover: {
    bg: 'surface.muted',
    color: 'text',
  },
  _active: {
    bg: 'surface.muted',
    color: 'text',
  },
}

const grayOutline = defineStyle((props) => {
  if (props.colorScheme !== 'gray') {
    return runVariantFn(defaultButtonVariants?.outline, props)
  }
  return {
    border: '1px solid',
    borderColor: 'border.control',
    ...grayInteractive,
    _hover: {
      ...grayInteractive._hover,
      borderColor: 'border.strong',
    },
    _active: {
      ...grayInteractive._active,
      borderColor: 'border.strong',
    },
  }
})

const grayGhost = defineStyle((props) => {
  if (props.colorScheme !== 'gray') {
    return runVariantFn(defaultButtonVariants?.ghost, props)
  }
  return grayInteractive
})

const graySolid = defineStyle((props) => {
  if (props.colorScheme !== 'gray') {
    return runVariantFn(defaultButtonVariants?.solid, props)
  }
  return {
    bg: 'surface.muted',
    color: 'text',
    border: '1px solid',
    borderColor: 'border',
    _hover: {
      bg: 'surface.muted',
      borderColor: 'border.strong',
      color: 'text',
    },
    _active: {
      bg: 'surface.muted',
      borderColor: 'border.strong',
    },
  }
})

export const buttonTheme = defineStyleConfig({
  baseStyle: {
    borderRadius: 'lg',
  },
  variants: {
    outline: grayOutline,
    ghost: grayGhost,
    solid: graySolid,
    link: (props) => runVariantFn(defaultButtonVariants?.link, props),
    unstyled: (props) => runVariantFn(defaultButtonVariants?.unstyled, props),
  },
})

const defaultBadgeVariants = chakraTheme.components?.Badge?.variants as
  | Record<
      string,
      ((props: StyleFunctionProps) => Record<string, unknown>) | Record<string, unknown>
    >
  | undefined

function grayBadgeVariant(
  variant: 'subtle' | 'outline' | 'solid',
  props: StyleFunctionProps
): Record<string, unknown> {
  if (props.colorScheme !== 'gray') {
    return runVariantFn(defaultBadgeVariants?.[variant], props)
  }
  if (variant === 'outline') {
    return {
      color: 'text.secondary',
      bg: 'transparent',
      borderWidth: '1px',
      borderColor: 'border.control',
    }
  }
  if (variant === 'solid') {
    return {
      bg: 'surface.muted',
      color: 'text.secondary',
    }
  }
  return {
    bg: 'surface.muted',
    color: 'text.secondary',
  }
}

export const inputTheme = {
  variants: {
    outline: {
      field: fieldOverrides,
    },
  },
  defaultProps: {
    focusBorderColor: 'accent',
  },
}

export const selectTheme = {
  variants: {
    outline: {
      field: fieldOverrides,
    },
  },
  defaultProps: {
    focusBorderColor: 'accent',
  },
}

export const textareaTheme = {
  variants: {
    outline: fieldOverrides,
  },
  defaultProps: {
    focusBorderColor: 'accent',
  },
}

export const numberInputTheme = {
  variants: {
    outline: {
      field: fieldOverrides,
    },
  },
  defaultProps: {
    focusBorderColor: 'accent',
  },
}

export const badgeTheme = defineStyleConfig({
  baseStyle: {
    borderRadius: 'full',
    px: 2.5,
    py: 0.5,
    fontWeight: 'semibold',
  },
  variants: {
    subtle: (props) => grayBadgeVariant('subtle', props),
    outline: (props) => grayBadgeVariant('outline', props),
    solid: (props) => grayBadgeVariant('solid', props),
  },
})

// Chakra's enclosed variant paints the selected tab's bottom border white so it
// visually merges with a white tab panel. On our shell the page canvas is main.bg,
// which created a white strip above blue-grey content on short pages like Info.
export const tabsTheme = {
  defaultProps: {
    colorScheme: 'brand',
  },
  variants: {
    enclosed: {
      tab: {
        borderTopRadius: 'lg',
        border: '1px solid',
        borderColor: 'transparent',
        mb: '-1px',
        color: 'text.secondary',
        _selected: {
          color: 'accent.strong',
          borderColor: 'border',
          borderBottomColor: 'main.bg',
          bg: 'main.bg',
        },
        _hover: {
          color: 'text',
        },
      },
      tablist: {
        mb: '-1px',
        borderBottom: '1px solid',
        borderColor: 'border',
      },
      tabpanel: {
        bg: 'transparent',
      },
    },
  },
}

export const dividerTheme = {
  baseStyle: {
    borderColor: 'border',
  },
}

// Header treatment mirroring the modernised PatientTable: quiet uppercase labels on
// semantic borders. Header/row backgrounds stay per-table (some use tinted sticky headers).
export const tableTheme = {
  variants: {
    simple: {
      th: {
        color: 'text.secondary',
        fontSize: 'xs',
        textTransform: 'uppercase',
        letterSpacing: 'wider',
        borderColor: 'border',
      },
      td: {
        borderColor: 'border',
      },
    },
  },
}
